import React from 'react';
import { LeadFormWizard, LeadFormWizardOnComplete } from '../forms/LeadFormWizard';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { RebrandAlert } from '@lendica/components';

const clientLogos = [
    {
        src: 'https://static.golendica.com/assets/img/southerneagle.png',
        alt: 'Southern Eagle Distributors',
        width: 152,
        height: 68,
    },
    {
        src: 'https://static.golendica.com/assets/img/atlas.png',
        alt: 'Atlas Refinery',
        width: 168,
        height: 54,
    },
    {
        src: 'https://static.golendica.com/assets/img/plaza.png',
        alt: 'The Plaza Group',
        width: 176,
        height: 45,
    },
    {
        src: 'https://static.golendica.com/assets/img/ecosolution.png',
        alt: 'Eco Solution Distributing',
        width: 196,
        height: 52,
    },
    {
        src: 'https://static.golendica.com/assets/img/roofbear.png',
        alt: 'Roof Bear',
        width: 104,
        height: 53,
    },
    {
        src: 'https://static.golendica.com/assets/img/southernenergy.png',
        alt: 'Southern Energy Distributors',
        width: 192,
        height: 45,
    },
    {
        src: 'https://static.golendica.com/assets/img/simplicitypower.png',
        alt: 'Simplicity Power',
        width: 169,
        height: 46,
    },
    {
        src: 'https://static.golendica.com/assets/img/surry.png',
        alt: 'Surry Chemicals',
        width: 127,
        height: 46,
    },
];

interface LeadCapturePageAProps {
    searchParams?: URLSearchParams;
    onComplete?: LeadFormWizardOnComplete;
    baseURL: string;
    requirePhone?: boolean;
    appType?: 'application' | 'dashboard';
    env?: string;
}

export const LeadCapturePageA = ({
    searchParams,
    onComplete,
    baseURL,
    requirePhone = true,
    appType = 'application',
    env = 'prod',
}: LeadCapturePageAProps) => {
    return (
        <Container>
            <Box
                sx={theme => ({
                    // Mobile: no flex centering, just full width
                    // Desktop: flex centering
                    display: { xs: 'block', md: 'flex' },
                    alignItems: { md: 'center' },
                    justifyContent: { md: 'center' },
                    width: '100%',
                    flex: { xs: 1, lg: 1.5 },
                    minHeight: '100vh',
                    maxHeight: { xs: 'auto', md: '100vh' },
                    p: { xs: 0, md: 3 }, // No padding on mobile, padding on desktop
                    position: 'relative',
                    borderRight: {
                        xs: 'none',
                        md: `1px solid ${theme.palette.divider}`,
                    },
                })}
            >
                <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, right: 0 }}>
                    <RebrandAlert sx={{ width: '100%', borderRadius: 0 }} />
                </Box>
                <LeadFormWizard
                    searchParams={searchParams}
                    onComplete={onComplete}
                    baseURL={baseURL}
                    requirePhone={requirePhone}
                    appType={appType}
                    env={env}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    width: '100%',
                    flex: 1,
                    maxWidth: {
                        xs: '100%',
                        md: 600,
                        lg: '100%',
                    },
                    background:
                        'radial-gradient(46.93% 58.5% at -2.57% 40.44%, rgba(244, 234, 225, 0.65) 0%, rgba(244, 234, 225, 0.00) 100%), linear-gradient(9deg, rgba(242, 237, 232, 0.35) 29.11%, rgba(255, 255, 255, 0.14) 87.57%), #FBF9F6',
                    px: 3,
                    py: 4,
                    minHeight: '100vh',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        maxWidth: 400,
                    }}
                >
                    <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 400 }}>
                        Join thousands of businesses powered by Daylit.
                    </Typography>
                    <ClientLogos />
                </Box>
            </Box>
        </Container>
    );
};

const ClientLogos = () => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                mixBlendMode: 'multiply',
            }}
        >
            {clientLogos.map(logo => (
                <img
                    key={logo.alt}
                    src={logo.src}
                    alt={logo.alt}
                    width={logo.width}
                    height={logo.height}
                />
            ))}
        </Box>
    );
};

const Container = ({ children }: { children: React.ReactNode }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'stretch',
                minHeight: '100vh',
                width: '100%',
                background: theme => theme.palette.background.paper,
            }}
        >
            {children}
        </Box>
    );
};
